/** @format */

import React, { useCallback, useEffect, useMemo } from 'react';
import TariffInterface from '../../../interfaces/billing/TariffInterface';
import BasePricelistInterface from '../../../interfaces/billing/BasePricelistInterface';
import Loader from '../../../components/Loaders/Loader';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableRow,
    Card,
    Button,
} from '@material-ui/core';
import I18n from '../../../components/Translate/I18n';
import { CustomMinimalTabs } from '../../../components/CustomMinimalTabs/CustomMinimalTabs';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { configDefault } from '../../../core/config';
import { tariffConstants } from '../../../modules/tariffs/actions';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { useDispatch } from 'react-redux';
import LinearBillingUpload, { LinearBillingUploadCollectionInterface } from '../../../interfaces/billing/LinearBillingUpload';
import PaperedTable from '../../../components/Table/PaperedTable';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import CancelButton from '../../../components/Buttons/CancelButton';
import PricelistInterface from '../../../interfaces/billing/PricelistInterface';
import { roles, UserRoleHelper } from '../../../helpers/UserRoleHelper';
import EditButton from '../../../components/Buttons/EditButton';
import DeleteButton from '../../../components/Buttons/DeleteButton';
import { DateHelperTs } from '../../../helpers/DateHelperTs';
import Pager from '../../../components/Adescom/Pager';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import { makeStyles } from '@material-ui/core/styles';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import CustomTextButton from '../../../components/Buttons/CustomTextButton';
import Paper from '@material-ui/core/Paper';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import CardBody from '../../../components/Card/CardBody';
import COLORS from '../../../assets/themes/OPL/colors';
import { UploadPricelistFile } from './UploadPricelistFile';
import FilterList from '../../../components/Filter/FilterList';
import { getPrefixUploadFileStatus, PREFIX_UPLOAD_STATUS } from '../../../modules/prefixes/helpers/PrefixUploadHelper';
import TemplateButton from '../../../components/Buttons/TemplateButton';
import { Report } from '@material-ui/icons';
import PrefixUploadReportView from '../../Prefixes/Import/PrefixUploadReportView';
import { getPricelistUploadFileStatus, PRICELIST_UPLOAD_STATUS } from '../../../modules/pricelists/helpers/PricelistUploadHelper';
import { LinearBillingUploadReportMessage } from '../../../interfaces/billing/LinearBillingUploadReportMessage';
import { PricelistUploadReport } from './PricelistUploadReport';

const useStyle = makeStyles(adescomStyle);

interface ImportListProps {
    open: boolean;
    tariff: TariffInterface;
    closeDialog: () => void;
}

export const ImportList = (props: ImportListProps): React.ReactElement => {
    const classes = useStyle();

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableCellRoot = { root: classes.tableCellRoot };
    const tableStickyCommand = { root: classes.tableStickyCommand };

    const open = props.open;
    const tariff = props.tariff;
    const closeDialog = props.closeDialog;
    const [loading, setLoading] = React.useState<boolean>(false);

    const dispatch = useDispatch();

    const [importId, setImportId] = React.useState<number>();
    const [selectedReportMessage, setSelectedReportMessage] = React.useState<LinearBillingUploadReportMessage | undefined>();
    const [reportOpen, setReportOpen] = React.useState<boolean>(false);

    const itemLoader = useMemo(() => {
        return new ItemLoaders(dispatch);
    }, [dispatch]);

    const [defaultFilters, setDefaultFilters] = React.useState<Array<Filter>>([
        {
            name: 'tariffId',
            value: tariff.id,
        },
    ]);
    const [filters, setFilters] = React.useState<Array<Filter>>([]);
    const [defaultOrders, setDefaultOrders] = React.useState<Array<Order>>([
        {
            name: 'importByDate',
            value: 'DESC',
        },
    ]);
    const [orders, setOrders] = React.useState<Array<Order>>([]);

    const onClose = useCallback((): void => {
        closeDialog();
    }, [closeDialog]);

    const [importList, setImportList] = React.useState<LinearBillingUploadCollectionInterface>();

    const getImports = useCallback((): void => {
        const tmpFilters = [...defaultFilters, ...filters];
        const tmpOrders = [...defaultOrders, ...orders];

        setLoading(true);
        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/linear_billing_uploads', configDefault.PLATFORM_API_PREFIX)),
            'PRICELIST_UPLOAD_LIST',
            tmpFilters,
            tmpOrders,
            {
                onSuccess: (data: any) => {
                    setLoading(false);
                    setImportList(data);
                },
                onError: () => {
                    setLoading(false);
                },
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    }, [itemLoader, defaultFilters, defaultOrders, filters, orders]);

    useEffect(() => {
        getImports();
    }, [getImports, orders, filters]);

    const paginationCallbackFunction = useCallback(
        (uri: string): void => {
            setLoading(true);
            const tmpFilters = [...defaultFilters, ...filters];
            const tmpOrders = [...defaultOrders, ...orders];

            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                tariffConstants.LIST,
                tmpFilters,
                tmpOrders,
                {
                    onSuccess: (data: any) => {
                        setLoading(false);
                        setImportList(data);
                    },
                    onError: () => {
                        setLoading(false);
                    },
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [itemLoader, filters, defaultOrders, defaultFilters, orders]
    );

    const updateFilters = useCallback(
        (newFilters: any): void => {
            setFilters(newFilters);
        },
        [setFilters]
    );

    const updateOrders = useCallback(
        (newOrders: any): void => {
            setOrders(newOrders);
        },
        [setOrders]
    );

    const reloadData = useCallback((): void => {
        if (!importList) {
            getImports();
        } else {
            paginationCallbackFunction(importList['hydra:view']['@id']);
        }
    }, [importList, getImports, paginationCallbackFunction]);

    const showReport = useCallback(
        (upload: LinearBillingUpload): void => {
            if (!upload.reportMessage) {
                return;
            }
            if (upload.reportMessage === '' || upload.reportMessage === '{}') {
                return;
            }
            setImportId(upload.id);
            setSelectedReportMessage((JSON.parse(upload.reportMessage) as unknown) as LinearBillingUploadReportMessage);
            setReportOpen(true);
        },
        [setSelectedReportMessage, setReportOpen]
    );

    const closeReportDialog = useCallback((): void => {
        setReportOpen(false);
        setImportId(undefined);
        setSelectedReportMessage(undefined);
    }, [setReportOpen, setSelectedReportMessage]);

    return (
        <>
            <Loader visible={loading} />
            <Dialog
                aria-labelledby="alert-dialog-slide-title"
                aria-describedby="alert-dialog-slide-description"
                open={open}
                onClose={onClose}
                maxWidth="lg"
            >
                <DialogTitle>
                    <I18n trans={'pricelists:import.pricelist_import'} />
                </DialogTitle>
                <DialogContent>
                    <GridContainer>
                        <GridItem xs={12}>
                            <UploadPricelistFile tariff={tariff} reloadData={reloadData} />
                        </GridItem>
                        <GridItem xs={12}>
                            <br />
                            <br />
                            <br />
                        </GridItem>
                        <GridItem xs={12}>
                            <GridContainer spacing={2}>
                                <GridItem xs={12} md={12} lg={12}>
                                    <PaperedTable
                                        beforeTableContent={
                                            <GridContainer>
                                                <GridItem xs={12} className={classes.beforeTableContent}>
                                                    <CustomTextButtom
                                                        callback={getImports}
                                                        callbackParam={{}}
                                                        color="primary"
                                                        label="general:common.search"
                                                        size="lg"
                                                    />
                                                    {(filters.length > 0 || orders.length > 0) && (
                                                        <CustomTextButtom
                                                            callback={(): void => {
                                                                setFilters([]);
                                                                setOrders([]);
                                                                getImports();
                                                            }}
                                                            callbackParam={{}}
                                                            color="primary"
                                                            label="general:common.clear"
                                                            size="lg"
                                                        />
                                                    )}
                                                    <FilterList />
                                                </GridItem>
                                            </GridContainer>
                                        }
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell classes={tableStickyCommand} padding="none" align="left">
                                                    <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                                                </TableCell>
                                                <TableCell classes={tableStickyHeader} align="left">
                                                    <TableHeaderContentCell
                                                        textAlign="left"
                                                        title="pricelists:import.filename"
                                                        updateOrderList={updateOrders}
                                                        updateFilterList={updateFilters}
                                                        fieldName="fileName"
                                                        showSort={true}
                                                        defaultDirection="desc"
                                                        searchFieldType="string"
                                                        filters={filters}
                                                        sort={orders}
                                                    />
                                                </TableCell>
                                                <TableCell classes={tableStickyHeader} align="left">
                                                    <TableHeaderContentCell
                                                        textAlign="left"
                                                        title="pricelists:import.status"
                                                        updateOrderList={updateOrders}
                                                        updateFilterList={updateFilters}
                                                        fieldName="status"
                                                        showSort={true}
                                                        defaultDirection="desc"
                                                        searchFieldType="select"
                                                        selectComponent="headerCheckBoxSimpleList"
                                                        elementList={[
                                                            {
                                                                name: 'pricelists:import_status.import_awaiting',
                                                                value: PRICELIST_UPLOAD_STATUS.STATUS_IMPORT_AWAIT,
                                                            },
                                                            {
                                                                name: 'pricelists:import_status.import_in_progress',
                                                                value: PRICELIST_UPLOAD_STATUS.STATUS_IMPORT_PROCESS,
                                                            },
                                                            {
                                                                name: 'pricelists:import_status.import_success',
                                                                value: PRICELIST_UPLOAD_STATUS.STATUS_IMPORT_FINISH,
                                                            },
                                                            {
                                                                name: 'pricelists:import_status.import_error',
                                                                value: PRICELIST_UPLOAD_STATUS.STATUS_ERROR,
                                                            },
                                                        ]}
                                                        filters={filters}
                                                        sort={orders}
                                                    />
                                                </TableCell>
                                                <TableCell classes={tableStickyHeader} align="left">
                                                    <TableHeaderContentCell
                                                        textAlign="left"
                                                        title="pricelists:import.added_date"
                                                        updateOrderList={updateOrders}
                                                        updateFilterList={updateFilters}
                                                        fieldName="importByDate"
                                                        defaultDirection="desc"
                                                        filters={filters}
                                                        sort={orders}
                                                        showSort={true}
                                                        onlyFilter={false}
                                                        searchFieldType="date"
                                                        dateFormat="RRRR-MM-DD"
                                                        beforeSetToMax={true}
                                                        afterSetToMin={true}
                                                        dateFromTo={true}
                                                        breakLine={true}
                                                        timeFormat={false}
                                                    />
                                                </TableCell>
                                                <TableCell classes={tableStickyHeader} align="left">
                                                    <TableHeaderContentCell
                                                        textAlign="left"
                                                        title="pricelists:import.import_date"
                                                        updateOrderList={updateOrders}
                                                        updateFilterList={updateFilters}
                                                        fieldName="importDate"
                                                        defaultDirection="desc"
                                                        filters={filters}
                                                        sort={orders}
                                                        showSort={true}
                                                        onlyFilter={false}
                                                        searchFieldType="date"
                                                        dateFormat="RRRR-MM-DD"
                                                        beforeSetToMax={true}
                                                        afterSetToMin={true}
                                                        dateFromTo={true}
                                                        breakLine={true}
                                                        timeFormat={false}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {importList &&
                                                importList['hydra:member'] &&
                                                importList['hydra:member'].map((importItem: LinearBillingUpload) => (
                                                    <TableRow key={importItem.id}>
                                                        <TableCell classes={tableCellRoot} padding="none" align="left">
                                                            <TemplateButton
                                                                icon={Report}
                                                                callback={showReport}
                                                                callbackParam={importItem}
                                                                tooltipTitle={
                                                                    [
                                                                        PRICELIST_UPLOAD_STATUS.STATUS_IMPORT_AWAIT,
                                                                        PRICELIST_UPLOAD_STATUS.STATUS_IMPORT_PROCESS,
                                                                    ].includes(importItem.status)
                                                                        ? 'pricelists:import.report_view_disabled'
                                                                        : 'pricelists:import.report_view_enabled'
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell classes={tableCellRoot} padding="none" align="left">
                                                            {importItem.fileName}
                                                        </TableCell>
                                                        <TableCell classes={tableCellRoot} padding="none" align="left">
                                                            <I18n trans={getPricelistUploadFileStatus(importItem.status)} />
                                                        </TableCell>
                                                        <TableCell classes={tableCellRoot} padding="none" align="left">
                                                            {DateHelperTs.format(importItem.importByDate)}
                                                        </TableCell>
                                                        <TableCell classes={tableCellRoot} padding="none" align="left">
                                                            {importItem.importDate && DateHelperTs.format(importItem.importDate)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                        <TableFooter>
                                            <TableRow>
                                                <TableCell classes={tableCellRoot} colSpan={4}>
                                                    {importList && importList['hydra:view'] && (
                                                        <Pager callback={paginationCallbackFunction} views={importList['hydra:view']} />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        </TableFooter>
                                    </PaperedTable>
                                </GridItem>
                                <GridItem xs={12}>
                                    {/*<PrefixUploadReportView prefixUploadFile={selectedPrefixUploadFile} closeReportView={closeReportView} />*/}
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                </DialogContent>
                <DialogActions></DialogActions>
            </Dialog>
            {reportOpen && selectedReportMessage && (
                <PricelistUploadReport open={reportOpen} report={selectedReportMessage} closeDialog={closeReportDialog} tariff={tariff} importId={importId} />
            )}
        </>
    );
};
